// React context + hook that exposes dynamic feature-access flags to
// every component. Source of truth: GET /api/features/access.
//
// Call `useFeatureAccess()` anywhere — returns:
//   { hasAccess, planId, loading, refetchAccess,
//     trialUsed, startTrial }
//
// The provider is mounted once inside the authenticated portion of the
// app so all downstream components share a single fetch.
//
// Refresh triggers (besides initial mount):
//   * `tadlo:subscription-changed` custom event — fired by checkout
//     flows after a successful upgrade.
//   * `visibilitychange → visible` — when the user returns to the tab,
//     refresh so an expired trial doesn't keep the UI unlocked
//     indefinitely while the tab sat in the background.
//
// Note (dev-only): React StrictMode double-invokes effects in
// development, which causes /api/features/access to fire twice on
// initial mount. This is documented React behaviour and does NOT
// happen in production builds — no code mitigation needed.
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { api } from "../App";

const FeatureAccessContext = createContext({
  hasAccess: () => false,
  planId: "starter",
  loading: true,
  refetchAccess: () => Promise.resolve(),
  trialUsed: () => false,
  startTrial: () => Promise.reject(new Error("Provider not mounted")),
});

export function FeatureAccessProvider({ children }) {
  const [state, setState] = useState({
    features: {},
    planId: "starter",
    trialsUsed: new Set(),
    loading: true,
  });
  const mountedRef = useRef(true);

  const fetchAccess = useCallback(async () => {
    // Aug 2026 — DO NOT call this authenticated endpoint without a token.
    //
    // This provider wraps the ENTIRE app, including the public link-in-bio
    // pages (tadlo.ai/{slug}), the landing page and the marketing pages. It
    // fired on mount, on every tab focus and every 60s regardless of login
    // state. For a logged-out visitor that is a guaranteed 401, and the axios
    // interceptor in App.js turns a 401 on a non-public path into
    // `window.location.href = "/auth"`.
    //
    // Net effect, reproduced on production 2026-08-11: opening
    // https://tadlo.ai/piyushnaik as an anonymous visitor bounced straight to
    // the login screen — on the one page type whose entire purpose is being
    // publicly viewable. Even a visitor who got past the first load would be
    // ejected within 60 seconds by the refresh interval.
    //
    // Logged out means "no features", which is what the empty initial state
    // already represents, so there is nothing to fetch.
    if (!localStorage.getItem("tadlo_token")) {
      if (mountedRef.current) setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const r = await api.get("/features/access");
      if (!mountedRef.current) return;
      const trials = Array.isArray(r.data?.trials_used) ? r.data.trials_used : [];
      setState({
        features: r.data?.features || {},
        planId: r.data?.plan_id || "starter",
        trialsUsed: new Set(trials),
        loading: false,
      });
    } catch {
      if (!mountedRef.current) return;
      // On error, fall back to "nothing enabled" — safer than fake
      // allowing. The PlanGate still renders its lock UI correctly.
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchAccess();
    return () => { mountedRef.current = false; };
  }, [fetchAccess]);

  // Custom event fired by the checkout flow.
  useEffect(() => {
    const handler = () => fetchAccess();
    window.addEventListener("tadlo:subscription-changed", handler);
    return () => window.removeEventListener("tadlo:subscription-changed", handler);
  }, [fetchAccess]);

  // Fired by login()/logout() in App.js. Required because fetchAccess now
  // short-circuits when there is no token: this provider sits ABOVE
  // BrowserRouter so it never remounts on navigation, and without this the
  // access map would stay empty after a successful login until the 60s
  // refresh interval happened to fire.
  useEffect(() => {
    const handler = () => fetchAccess();
    window.addEventListener("tadlo:auth-changed", handler);
    return () => window.removeEventListener("tadlo:auth-changed", handler);
  }, [fetchAccess]);

  // Refresh when the user returns to the tab. This catches expired
  // trials that lapsed while the tab was in the background.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchAccess();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchAccess]);

  // Bug 9b — periodic refresh so a master-admin global-disable
  // propagates to active sessions within a minute, even if the user
  // never switches tabs. 60s is a sweet spot — fast enough that an
  // ops-side kill switch feels immediate, slow enough to be invisible
  // in the network panel for a normal session.
  useEffect(() => {
    const id = setInterval(fetchAccess, 60000);
    return () => clearInterval(id);
  }, [fetchAccess]);

  const hasAccess = useCallback(
    (featureId) => !!state.features[featureId],
    [state.features],
  );
  const trialUsed = useCallback(
    (featureId) => state.trialsUsed.has(featureId),
    [state.trialsUsed],
  );
  const startTrial = useCallback(async (featureId) => {
    const r = await api.post(`/features/trial/${featureId}`);
    // Pull the fresh access map (includes trials_used) so the UI
    // unlocks the section immediately.
    await fetchAccess();
    return r.data;
  }, [fetchAccess]);

  const value = {
    hasAccess,
    planId: state.planId,
    loading: state.loading,
    refetchAccess: fetchAccess,
    trialUsed,
    startTrial,
  };

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
    </FeatureAccessContext.Provider>
  );
}

export function useFeatureAccess() {
  return useContext(FeatureAccessContext);
}

// Fire-and-forget helper for any code path that completes a plan
// change — triggers a refetch across the app without needing to
// import the hook. Used by pages/PricingPage.js after a successful
// upgrade + Stripe webhook callback.
export function notifySubscriptionChanged() {
  window.dispatchEvent(new CustomEvent("tadlo:subscription-changed"));
}
